import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProductDetail, recordProductView } from './useCatalogue';
import { ProductThumb } from './ProductThumb';
import { PageLoader, ErrorState } from '../components/ui';
import { money, weight } from '../lib/format';
import { useCart } from '../store/cart';
import { useToast } from '../components/Toast';
import { ApiRequestError } from '../lib/api';

export function ProductPage() {
  const { qrToken, assignmentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useProductDetail(assignmentId);
  const add = useCart((s) => s.add);

  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    if (assignmentId) recordProductView(assignmentId);
  }, [assignmentId]);

  if (isLoading) return <PageLoader label="Loading the piece…" />;
  if (error || !data)
    return (
      <div className="p-6">
        <ErrorState
          message={error instanceof ApiRequestError ? error.message : 'This item is unavailable.'}
          onRetry={() => refetch()}
        />
      </div>
    );

  const cap = data.availableQuantity;
  const images = data.images.length ? data.images : [];

  const addToCart = (goCheckout: boolean) => {
    add(
      {
        assignmentId: data.assignmentId,
        productId: data.productId,
        name: data.name,
        barcodeNo: data.barcodeNo,
        photoId: data.photoId,
        price: data.price,
        image: data.image,
        size: data.size,
        grossWeight: data.grossWeight,
        stoneWeight: data.stoneWeight,
        stoneAmount: data.stoneAmount,
        displayOrder: data.displayOrder,
        inStock: data.inStock,
        availableQuantity: data.availableQuantity,
      },
      data.rack.number ?? '',
      qty,
    );
    if (goCheckout) navigate(`/r/${qrToken}/checkout`);
    else toast(`${qty} × added to cart`, 'success');
  };

  return (
    <div className="pb-28">
      <div className="aspect-square w-full bg-ink-50">
        {images.length ? (
          <ProductThumb src={images[activeImg]?.url} alt={data.name} label={data.photoId} />
        ) : (
          <ProductThumb src={null} alt={data.name} label={data.photoId} />
        )}
      </div>

      {images.length > 1 && (
        <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-3">
          {images.map((im, i) => (
            <button
              key={im.url}
              onClick={() => setActiveImg(i)}
              className={`h-14 w-14 shrink-0 overflow-hidden rounded-md border ${
                i === activeImg ? 'border-gold-500' : 'border-ink-100'
              }`}
            >
              <img src={im.thumbnailUrl} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="space-y-4 px-4 pt-4">
        <div>
          <h1 className="text-xl font-semibold text-ink-900">{data.name}</h1>
          <p className="mt-0.5 text-sm text-ink-400">
            Barcode #{data.barcodeNo}
            {data.photoId ? ` · ${data.photoId}` : ''}
          </p>
        </div>

        <p className="text-2xl font-semibold text-gold-600">
          {data.price ? money(data.price, true) : 'Price on enquiry'}
        </p>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl bg-ink-50 p-3 text-sm">
          {data.size && <Row label="Size" value={data.size} />}
          <Row label="Gross weight" value={weight(data.grossWeight)} />
          {data.stoneWeight && <Row label="Stone weight" value={weight(data.stoneWeight)} />}
          {data.stoneAmount && <Row label="Stone amount" value={money(data.stoneAmount, true)} />}
          {data.category && <Row label="Category" value={data.category} />}
          <Row
            label="Availability"
            value={
              data.inStock
                ? cap != null
                  ? `${cap} available`
                  : 'In stock'
                : 'Sold out'
            }
          />
        </dl>

        {data.description && (
          <div>
            <h2 className="mb-1 text-sm font-semibold text-ink-700">Description</h2>
            <p className="text-sm leading-relaxed text-ink-500">{data.description}</p>
          </div>
        )}
      </div>

      {/* sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-xl border-t border-ink-100 bg-white px-4 py-3">
        {data.inStock ? (
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-lg border border-ink-200">
              <button
                className="px-3 py-2 text-lg text-ink-600 disabled:opacity-40"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                disabled={qty <= 1}
              >
                −
              </button>
              <span className="w-8 text-center text-sm font-medium">{qty}</span>
              <button
                className="px-3 py-2 text-lg text-ink-600 disabled:opacity-40"
                onClick={() => setQty((q) => Math.min(cap ?? 99, q + 1))}
                disabled={cap != null && qty >= cap}
              >
                +
              </button>
            </div>
            <button className="btn-outline flex-1" onClick={() => addToCart(false)}>
              Add to cart
            </button>
            <button className="btn-gold flex-1" onClick={() => addToCart(true)}>
              Book now
            </button>
          </div>
        ) : (
          <button className="btn-outline w-full" disabled>
            Currently sold out
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className="font-medium text-ink-800">{value}</dd>
    </div>
  );
}
